/**
 * This is a custom version of prompt list used for indexing and forms.  The main difference from PromptList2 is that it
 * does not extend from DFValidator, and adds additional behavior for things like replacing inputs.
 * The validator implementation is completely model based and supports warnings/overrides.
 */
import PromptList from "common/widgets/PromptList/CustomPromptList";
import PromptInputs from "app/widgets/PromptList/PromptInputDefinitions";
import ResponsivePromptList from "app/widgets/PromptList/ResponsivePromptList";
import ElementNodeType from "common/enums/ElementNodeType";

var CONTAINER_STYLES = [
    "borderColor",
    "borderSize",
    "backgroundColor",
    "fontSize",
    "fontFamily",
    "fontWeight",
    "fontStyle",
    "fontColor"
];

const CustomPromptList = PromptList.extend(PromptInputs).extend(ResponsivePromptList).extend({

    // cache container styles so they can be toggled on and off
    _containerStyles: undefined,

    /**
     * Override
     */
    init: function() {
        PromptList.fn.init.apply(this, arguments);
        this._containerStyles = {};
    },

    setIsThemed(isThemed) {
        this._isThemed = isThemed;
        this._setPromptStyles();
        this.promptWidgets.forEach((promptInput) => {
            promptInput.hasTheme(isThemed);
        });
    },

    promptInputOptions() {
        return {
            hasTheme: this._isThemed
        }
    },

    /**
     * Cache the styles of the PromptList
     * @param styles
     * @private
     */
    _setContainerStyles: function(styles) {
        var me = this;
        CONTAINER_STYLES.forEach(function(style) {
            me.setStyle(style, styles[style]);
        })
    },

    /**
     * Set a single style for the PromptList container
     * @param style {string} The style to set
     * @param value {string} The value of the style
     */
    setStyle: function(style, value) {
        this._containerStyles[style] = value;
    },

    /**
     * Set the styles of the PromptList to the styles passed
     * @param promptContainer {*} The styles to set on the PromptList
     */
    setPromptStyles: function(promptContainer) {
        this._setContainerStyles(promptContainer);
        this._setPromptStyles();
    },

    /**
     * Set the styles of the PromptList
     * @private
     */
    _setPromptStyles: function() {
        var me = this;
        var borderStyle = "";
        var width = "100%";
        // Set form styles from info retrieved from render call - border
        if (this._containerStyles.borderColor && this._containerStyles.borderSize) {
            var borderSize = parseInt(this._containerStyles.borderSize) + "px";
            width = "calc(100% - " + borderSize + " - " + borderSize + ")";
            borderStyle = borderSize + " solid " + this._containerStyles.borderColor;
            this.setBorderSize(borderSize);
        } else {
            borderStyle = "none";
            this.setBorderSize("0px");
        }
        me.element.css("border", borderStyle);
        me.element.css("width", width);

        // Set form styles from info retrieved from render call - background
        me.element.css("background-color", this._containerStyles.backgroundColor !== "" ? this._containerStyles.backgroundColor : "transparent");
        // Set form styles from info retrieved from render call - font styles on form.
        // Can be overridden by the same styles on individual form controls.
        me.element.removeClass(function(i, css) {
            return (css.match(/(\bfont\d+\b)/g) || []).join(" ");
        });
        me.element.removeClass("verdana times arial");
        me.element.removeClass("bold");
        me.element.removeClass("italic");
        if(this._isThemed) {
            return;
        }
        if (this._containerStyles.fontSize) {
            me.element.addClass("font" + this._containerStyles.fontSize);
        }
        if (this._containerStyles.fontFamily) {
            me.element.addClass(this._containerStyles.fontFamily);
        }
        if (this._containerStyles.fontWeight === "bold") {
            me.element.addClass("bold");
        }
        if (this._containerStyles.fontStyle === "italic") {
            me.element.addClass("italic");
        }
        if (this._containerStyles.fontColor) {
            me.element.css("color", this._containerStyles.fontColor);
        } else {
            me.element.css("color", "");
        }
    },

    _updateFlex: function(input) {
        input.updateFlex();
    },

    /**
     * Sets the styles on prompt inputs
     * @param targetElement {$} The container of the prompt
     * @param prompt {*} The PromptInput
     * @param lbl {$} The label of the prompt
     */
    setPromptControlStyles: function(targetElement, prompt, lbl) {
        var className = prompt.labelPosition;
        if (className && className.toLowerCase) {
            targetElement.addClass(className.toLowerCase() + "Label");
        }
        if (prompt.label == undefined || prompt.label == null || prompt.label.length == 0) {
            targetElement.addClass("noLabel");
        }

        lbl.toggleClass("bold", !this._isThemed && prompt.isBold === true);
        lbl.toggleClass("unbold", !this._isThemed && prompt.isBold === false);

        lbl.toggleClass("italic", !this._isThemed && prompt.isItalic === true);
        lbl.toggleClass("unitalic", !this._isThemed && prompt.isItalic === false);

        if(!this._isThemed) {
            if(prompt.color && lbl) {
                lbl.css("color", "#" + prompt.color);
            }
            if(prompt.fontFamily) {
                targetElement.addClass(prompt.fontFamily);
            }
            if(prompt.fontSize) {
                targetElement.addClass("font" + prompt.fontSize);
            }
        }
        if (prompt.textStyle) {
            targetElement.addClass(ElementNodeType.styleName(prompt.textStyle));
        }
    }

});
kendo.ui.plugin(CustomPromptList);
export default CustomPromptList;
